import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { useAuth } from '../hooks/useAuth';
import { isSuperAdmin } from '../utils/roles';

const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

interface InboxMessage {
  id: string;
  mailbox: string;
  from: string;
  subject: string;
  preview: string;
  receivedAt: string;
  isRead: boolean;
  webLink?: string;
}

interface MailboxConfig {
  upn: string;
  label: string;
}

function authHeaders(): HeadersInit {
  return { Authorization: `Bearer ${localStorage.getItem('intrlock_token') ?? ''}` };
}

/**
 * Communication / M365 Inbox
 * --------------------------
 * App-only Graph reads admin@/support@/distribution@ mailboxes via the
 * Entra app registration "Intrlock Web — admin inbox". Per-mailbox
 * access is restricted by an Exchange Online Application Access Policy.
 */
export const AdminInbox: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mailboxes, setMailboxes] = useState<MailboxConfig[]>([]);
  const [activeMailbox, setActiveMailbox] = useState<string>(''); // '' = all combined
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingReason, setPendingReason] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePrefill, setComposePrefill] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [folders, setFolders] = useState<Array<{ id: string; displayName: string; unreadItemCount: number; totalItemCount: number }>>([]);
  const [activeFolderId, setActiveFolderId] = useState<string>('');
  // When "All" is selected, show folders per-mailbox in a stacked view.
  const [allFolders, setAllFolders] = useState<Record<string, Array<{ id: string; displayName: string; unreadItemCount: number; totalItemCount: number }>>>({});

  // Strip the From header into a plain email for reply targeting.
  const addrOf = (s: string) => {
    const m = String(s || '').match(/<([^>]+)>/);
    return m ? m[1] : (s || '').trim();
  };
  // Build a quoted-body the recipient can read inline. Plain-text so it
  // works whether the original was HTML or text.
  const quoteBody = (d: any) => {
    const dt = d.receivedAt ? new Date(d.receivedAt).toLocaleString() : '';
    const preview = (d.bodyPreview || '').slice(0, 4000);
    return `\n\n----- Original Message -----\nFrom: ${d.from}\nSent: ${dt}\nTo: ${(d.toRecipients || []).join(', ')}${d.ccRecipients?.length ? `\nCc: ${d.ccRecipients.join(', ')}` : ''}\nSubject: ${d.subject}\n\n${preview}\n`;
  };

  const openReply = (all: boolean) => {
    if (!detail) return;
    setComposePrefill({
      mode: all ? 'replyAll' : 'reply',
      from: detail.mailbox,
      to: addrOf(detail.from),
      cc: all ? (detail.ccRecipients || []).map(addrOf).join(', ') : '',
      subject: detail.subject?.startsWith('Re:') ? detail.subject : `Re: ${detail.subject}`,
      body: '',
      replyToMessageId: detail.id,
    });
    setDetail(null);
    setComposeOpen(true);
  };
  const openForward = () => {
    if (!detail) return;
    setComposePrefill({
      mode: 'forward',
      from: detail.mailbox,
      to: '',
      subject: detail.subject?.startsWith('Fwd:') ? detail.subject : `Fwd: ${detail.subject}`,
      body: quoteBody(detail),
      forwardMessageId: detail.id,
    });
    setDetail(null);
    setComposeOpen(true);
  };

  const openMessage = async (m: InboxMessage) => {
    setDetail({ loading: true, mailbox: m.mailbox, subject: m.subject });
    setDetailLoading(true);
    try {
      const [detailRes, notesRes] = await Promise.all([
        fetch(`${API}/admin/inbox/message?mailbox=${encodeURIComponent(m.mailbox)}&id=${encodeURIComponent(m.id)}`, { headers: authHeaders() }),
        fetch(`${API}/admin/inbox/notes?mailbox=${encodeURIComponent(m.mailbox)}&id=${encodeURIComponent(m.id)}`,   { headers: authHeaders() }),
      ]);
      const data  = await detailRes.json();
      const notes = await notesRes.json();
      setDetail({ ...data, notes: notes.notes || [], assignments: notes.assignments || [] });
    } catch (e: any) {
      setDetail({ error: e.message ?? String(e) });
    } finally { setDetailLoading(false); }
  };

  // Mark current message read/unread and update the list inline.
  const setRead = async (isRead: boolean) => {
    if (!detail?.id) return;
    await fetch(`${API}/admin/inbox/message/read`, {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ mailbox: detail.mailbox, id: detail.id, isRead }),
    });
    setDetail({ ...detail, isRead });
    setMessages((prev) => prev.map(m => m.id === detail.id ? { ...m, isRead } : m));
  };

  // Add an internal note.
  const addNote = async (body: string) => {
    if (!body.trim() || !detail?.id) return;
    const res = await fetch(`${API}/admin/inbox/notes`, {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ mailbox: detail.mailbox, id: detail.id, body }),
    });
    const note = await res.json();
    if (note?.id) setDetail({ ...detail, notes: [...(detail.notes || []), note] });
  };

  // Assign to an entity.
  const addAssignment = async (entity_type: string, entity_id: string, entity_name: string) => {
    if (!detail?.id) return;
    const res = await fetch(`${API}/admin/inbox/assignments`, {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ mailbox: detail.mailbox, id: detail.id, entity_type, entity_id }),
    });
    const data = await res.json();
    if (data?.id) setDetail({ ...detail, assignments: [...(detail.assignments || []), { ...data, entity_name }] });
  };
  const removeAssignment = async (id: string) => {
    await fetch(`${API}/admin/inbox/assignments/delete`, {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    setDetail({ ...detail, assignments: (detail.assignments || []).filter((a: any) => a.id !== id) });
  };

  useEffect(() => {
    if (user && !isSuperAdmin(user.role as any)) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Load mailboxes once
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/inbox/mailboxes`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setMailboxes(Array.isArray(data) ? data : []);
        }
      } catch { /* fall through to load() */ }
    })();
  }, []);

  // Load mail folders. When a single mailbox is active, load just that
  // mailbox's folders. When "All" is active, load folders for every
  // configured mailbox in parallel for the per-mailbox folder bar.
  useEffect(() => {
    setActiveFolderId('');
    setFolders([]);
    if (activeMailbox) {
      fetch(`${API}/admin/inbox/folders?mailbox=${encodeURIComponent(activeMailbox)}`, { headers: authHeaders() })
        .then(r => r.json())
        .then(d => { if (Array.isArray(d)) setFolders(d); })
        .catch(() => {});
      return;
    }
    // All view — fetch folders per mailbox
    if (mailboxes.length === 0) return;
    Promise.all(
      mailboxes.map(m =>
        fetch(`${API}/admin/inbox/folders?mailbox=${encodeURIComponent(m.upn)}`, { headers: authHeaders() })
          .then(r => r.json())
          .then(d => [m.upn, Array.isArray(d) ? d : ([] as any[])] as [string, any[]])
          .catch(() => [m.upn, [] as any[]] as [string, any[]])
      )
    ).then(pairs => {
      const map: Record<string, any[]> = {};
      for (const [upn, fs] of pairs) map[upn] = fs;
      setAllFolders(map);
    });
  }, [activeMailbox, mailboxes]);

  // Load messages when mailbox or folder changes
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      setPendingReason(null);
      try {
        const url = activeFolderId && activeMailbox
          ? `${API}/admin/inbox/folder-messages?mailbox=${encodeURIComponent(activeMailbox)}&folderId=${encodeURIComponent(activeFolderId)}`
          : activeMailbox
          ? `${API}/admin/inbox/messages?mailbox=${encodeURIComponent(activeMailbox)}`
          : `${API}/admin/inbox/messages`;
        const res = await fetch(url, { headers: authHeaders() });
        const data = await res.json();
        if (data?.pending) {
          setPendingReason(data.reason ?? 'M365 wiring pending.');
          setMessages([]);
        } else if (Array.isArray(data)) {
          // Single-mailbox shape (array)
          setMessages(data);
        } else if (data && Array.isArray(data.messages)) {
          // Combined shape: { messages, mailbox_errors[] }
          setMessages(data.messages);
          if (data.mailbox_errors?.length) {
            setError(
              `Microsoft 365 returned errors for ${data.mailbox_errors.length} mailbox(es) — likely the Entra app's Exchange Online Application Access Policy isn't scoped to these mailboxes yet. ` +
              data.mailbox_errors.map((e: any) => `${e.upn}: ${(e.error.match(/"code":"([^"]+)"/) || [])[1] || 'error'}`).join(' · ')
            );
          }
        } else if (data?.error) {
          setError(data.error);
          setMessages([]);
        } else {
          setMessages([]);
        }
      } catch (e: any) {
        setError(e.message ?? String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeMailbox, activeFolderId]);

  return (
    <div className="shadow-page-frame space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Communication</h1>
          <p className="text-sm text-gray-500">Microsoft 365 mailboxes — admin, support, distribution{mailboxes.length > 3 ? ` + ${mailboxes.length - 3} more` : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge color={pendingReason ? 'orange' : 'green'}>{pendingReason ? 'pending Entra setup' : 'live'}</Badge>
          {!pendingReason && <Button onClick={() => setComposeOpen(true)}>+ Compose</Button>}
        </div>
      </div>

      {/* Mailbox picker */}
      {mailboxes.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setActiveMailbox('')}
            className={`px-3 py-1.5 rounded text-sm font-medium ${activeMailbox === '' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            All ({mailboxes.length})
          </button>
          {mailboxes.map((m) => {
            const active = activeMailbox === m.upn;
            return (
              <button
                key={m.upn}
                onClick={() => setActiveMailbox(m.upn)}
                className={`px-3 py-1.5 rounded text-sm leading-tight text-left ${active ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                title={m.upn}
              >
                <span className={`block font-mono text-[11px] ${active ? 'text-emerald-100' : 'text-gray-500'}`}>{m.upn}</span>
                <span className="block font-semibold">{m.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {pendingReason && (
        <Card>
          <div className="p-4 border-b">
            <h3 className="font-semibold">Inbox is not wired yet</h3>
            <p className="text-sm text-gray-500 mt-1">Backend says: <code className="bg-gray-100 px-1 rounded text-xs">{pendingReason}</code></p>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <p>The Entra app exists; you still need to:</p>
            <div className="flex items-start gap-2"><span className="text-gray-400">1.</span><span>Create the client secret on the Entra app (<strong>Add a certificate or secret</strong> on the app's overview page)</span></div>
            <div className="flex items-start gap-2"><span className="text-gray-400">2.</span><span>Paste the secret value into AWS Secrets Manager: <code className="bg-gray-100 px-1 rounded text-xs">/intrlock/prod/m365/oauth</code></span></div>
            <div className="flex items-start gap-2"><span className="text-gray-400">3.</span><span>Add Microsoft Graph <strong>Application</strong> permissions: <code className="bg-gray-100 px-1 rounded text-xs">Mail.Read</code>, <code className="bg-gray-100 px-1 rounded text-xs">Mail.Send</code>, <code className="bg-gray-100 px-1 rounded text-xs">User.Read.All</code> + admin consent</span></div>
            <div className="flex items-start gap-2"><span className="text-gray-400">4.</span><span>Run the Exchange Online Application Access Policy command (see provided PowerShell snippet) to restrict the app to <code className="bg-gray-100 px-1 rounded text-xs">admin/support/distribution@intrlock.io</code> only</span></div>
            <div className="pt-2 flex gap-2">
              <Button variant="secondary" onClick={() => navigate('/admin')}>← Back to Admin Dashboard</Button>
            </div>
          </div>
        </Card>
      )}

      {error && (
        <Card>
          <div className="p-4 text-red-700 bg-red-50 text-sm">{error}</div>
        </Card>
      )}

      <ComposeModal
        open={composeOpen}
        onClose={() => { setComposeOpen(false); setComposePrefill(null); }}
        mailboxes={mailboxes}
        defaultFrom={activeMailbox || mailboxes[0]?.upn || ''}
        prefill={composePrefill || undefined}
      />

      {activeMailbox && folders.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mb-2 text-xs">
          <span className="text-gray-500 mr-2">Folders ({activeMailbox}):</span>
          <button
            onClick={() => setActiveFolderId('')}
            className={`px-2 py-1 rounded ${!activeFolderId ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            All
          </button>
          {folders
            .filter(f => f.totalItemCount > 0 || ['Inbox','Sent Items','Drafts','Junk Email','Archive','Deleted Items'].includes(f.displayName))
            .sort((a, b) => b.totalItemCount - a.totalItemCount)
            .map(f => (
              <button key={f.id} onClick={() => setActiveFolderId(f.id)}
                className={`px-2 py-1 rounded ${activeFolderId === f.id ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f.displayName}
                {f.unreadItemCount > 0 && <span className="ml-1 font-semibold">({f.unreadItemCount})</span>}
              </button>
            ))}
        </div>
      )}

      {/* "All" view — one row per mailbox with its folders to the right.
          Clicking a folder switches to that mailbox AND scopes to that folder. */}
      {!activeMailbox && Object.keys(allFolders).length > 0 && (
        <div className="border border-gray-200 rounded-lg p-3 mb-2 bg-gray-50 space-y-2">
          {mailboxes.map(m => {
            const fs = allFolders[m.upn] || [];
            const standard = fs.filter(f => f.totalItemCount > 0 || ['Inbox','Sent Items','Drafts','Junk Email','Archive','Deleted Items'].includes(f.displayName))
                              .sort((a, b) => b.totalItemCount - a.totalItemCount);
            return (
              <div key={m.upn} className="flex flex-wrap items-center gap-1 text-xs">
                <span className="font-mono font-semibold text-gray-800 mr-1 min-w-[170px]">{m.upn}:</span>
                <button onClick={() => { setActiveMailbox(m.upn); setActiveFolderId(''); }}
                  className="px-2 py-1 rounded bg-gray-200 text-gray-800 hover:bg-emerald-600 hover:text-white">
                  All
                </button>
                {standard.map(f => (
                  <button key={f.id} onClick={() => { setActiveMailbox(m.upn); setActiveFolderId(f.id); }}
                    className="px-2 py-1 rounded bg-gray-200 text-gray-800 hover:bg-emerald-600 hover:text-white">
                    {f.displayName}
                    {f.unreadItemCount > 0 && <span className="ml-1 font-semibold">({f.unreadItemCount})</span>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {!pendingReason && !error && (
        <Card padding={false}>
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Loading messages…</div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No messages</div>
          ) : (
            <div className="divide-y">
              {messages.map(m => (
                <div key={m.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer ${m.isRead ? '' : 'bg-emerald-50/40'}`} onClick={() => openMessage(m)}>
                  <div className="flex items-baseline justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{m.from}</span>
                      {!activeMailbox && <Badge color="gray">{m.mailbox.split('@')[0]}</Badge>}
                    </div>
                    <div className="text-xs text-gray-400">{new Date(m.receivedAt).toLocaleString()}</div>
                  </div>
                  <div className="text-sm font-semibold mt-0.5">{m.subject}</div>
                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">{m.preview}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Email detail modal — full body, recipients, attachments. Opens
          inline so we don't have to round-trip through Outlook on the web. */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.subject || 'Email'} size="lg">
        {detailLoading && !detail?.body ? (
          <div className="p-6 text-center text-sm text-gray-400">Loading message…</div>
        ) : detail?.error ? (
          <div className="p-4 text-sm text-red-600">{detail.error}</div>
        ) : detail ? (
          <div className="space-y-3 text-sm">
            <div className="border-b border-gray-200 pb-3">
              <div className="grid grid-cols-[60px_1fr] gap-y-1 text-xs">
                <span className="text-gray-500">From</span><span className="font-medium">{detail.from}</span>
                <span className="text-gray-500">To</span>  <span>{(detail.toRecipients || []).join(', ') || '—'}</span>
                {detail.ccRecipients?.length > 0 && (<><span className="text-gray-500">Cc</span><span>{detail.ccRecipients.join(', ')}</span></>)}
                <span className="text-gray-500">Mailbox</span><Badge color="gray">{detail.mailbox}</Badge>
                <span className="text-gray-500">Received</span><span>{detail.receivedAt && new Date(detail.receivedAt).toLocaleString()}</span>
              </div>
              {detail.hasAttachments && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {(detail.attachments || []).map((a: any) => (
                    <Badge key={a.id} color="green">📎 {a.name} ({Math.round(a.size / 1024)} KB)</Badge>
                  ))}
                </div>
              )}
            </div>
            {detail.bodyType === 'html' ? (
              <iframe
                title="email body"
                srcDoc={detail.body}
                sandbox="allow-popups allow-same-origin"
                className="w-full min-h-[400px] border border-gray-100 rounded"
              />
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 p-3 rounded border border-gray-100">{detail.body}</pre>
            )}
            {/* Internal notes + entity assignments */}
            <EmailMetaSection
              detail={detail}
              onAddNote={addNote}
              onAssign={addAssignment}
              onUnassign={removeAssignment}
            />

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => openReply(false)}>↩ Reply</Button>
                {(detail.ccRecipients?.length > 0 || detail.toRecipients?.length > 1) && (
                  <Button variant="secondary" onClick={() => openReply(true)}>↩↩ Reply All</Button>
                )}
                <Button variant="secondary" onClick={openForward}>→ Forward</Button>
                <Button variant="secondary" onClick={() => setRead(!detail.isRead)}>
                  {detail.isRead ? '◯ Mark Unread' : '✓ Mark Read'}
                </Button>
              </div>
              <div className="flex items-center gap-3">
                {detail.webLink && <a href={detail.webLink} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 hover:underline">Open in Outlook ↗</a>}
                <Button variant="secondary" onClick={() => setDetail(null)}>Close</Button>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

// Compose mode tells the modal what flavor of message it's writing — affects
// which fields prefill, the title, and whether the API call uses /reply,
// /forward, or /sendMail under the hood.
type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward';
interface ComposePrefill {
  mode: ComposeMode;
  from?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body?: string;
  replyToMessageId?: string;
  forwardMessageId?: string;
}
interface ComposeProps {
  open: boolean;
  onClose: () => void;
  mailboxes: MailboxConfig[];
  defaultFrom: string;
  prefill?: ComposePrefill;
}

const ComposeModal: React.FC<ComposeProps> = ({ open, onClose, mailboxes, defaultFrom, prefill }) => {
  const mode = prefill?.mode || 'new';
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo]   = useState('');
  const [cc, setCc]   = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc,  setShowCc]  = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When the modal opens, hydrate every field from prefill so reply/forward
  // arrive pre-populated and the user just adds their message.
  useEffect(() => {
    if (!open) return;
    setFrom(prefill?.from   ?? defaultFrom);
    setTo  (prefill?.to     ?? '');
    setCc  (prefill?.cc     ?? '');
    setBcc (prefill?.bcc    ?? '');
    setSubject(prefill?.subject ?? '');
    setBody(prefill?.body   ?? '');
    setShowCc(!!prefill?.cc);
    setShowBcc(!!prefill?.bcc);
    setError(null);
  }, [open]);

  const title =
    mode === 'reply'    ? 'Reply' :
    mode === 'replyAll' ? 'Reply All' :
    mode === 'forward'  ? 'Forward' :
                          'Compose email';

  const send = async () => {
    setSending(true); setError(null);
    try {
      const res = await fetch(`${API}/admin/inbox/send`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from,
          to:  to.split(',').map((s) => s.trim()).filter(Boolean),
          cc:  cc.split(',').map((s) => s.trim()).filter(Boolean),
          bcc: bcc.split(',').map((s) => s.trim()).filter(Boolean),
          subject,
          body,
          isHtml: false,
          replyToMessageId: prefill?.replyToMessageId,
          forwardMessageId: prefill?.forwardMessageId,
        }),
      });
      const data = await res.json();
      if (!res.ok || data?.error || data?.detail) throw new Error(data.error || data.detail || `send ${res.status}`);
      onClose();
      setTo(''); setCc(''); setBcc(''); setSubject(''); setBody('');
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title} size="lg">
      <div className="space-y-3">
        <div>
          <label className="block text-xs uppercase text-gray-500 mb-1">From</label>
          <select value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5">
            {mailboxes.map((m) => <option key={m.upn} value={m.upn}>{m.label} &lt;{m.upn}&gt;</option>)}
          </select>
        </div>
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <Input label="To (comma-separated)" value={to} onChange={(e: any) => setTo(e.target.value)} placeholder="alice@example.com, bob@example.com" />
          </div>
          <div className="flex gap-1 mt-6">
            {!showCc  && <button type="button" onClick={() => setShowCc(true)}  className="text-xs text-emerald-700 hover:underline">+ Cc</button>}
            {!showBcc && <button type="button" onClick={() => setShowBcc(true)} className="text-xs text-emerald-700 hover:underline">+ Bcc</button>}
          </div>
        </div>
        {showCc  && <Input label="Cc"  value={cc}  onChange={(e: any) => setCc(e.target.value)} />}
        {showBcc && <Input label="Bcc" value={bcc} onChange={(e: any) => setBcc(e.target.value)} />}
        <Input label="Subject" value={subject} onChange={(e: any) => setSubject(e.target.value)} />
        <div>
          <label className="block text-xs uppercase text-gray-500 mb-1">Body</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="w-full border border-gray-300 rounded px-2 py-1.5 font-mono text-sm" />
        </div>
        {error && <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">{error}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending || !to || !subject || (mode !== 'reply' && mode !== 'replyAll' && !body)}>{sending ? 'Sending…' : title}</Button>
        </div>
      </div>
    </Modal>
  );
};

// ── Email Meta — notes + entity assignments inside the detail modal ──
const EmailMetaSection: React.FC<{
  detail: any;
  onAddNote: (b: string) => Promise<void>;
  onAssign: (type: string, id: string, name: string) => Promise<void>;
  onUnassign: (id: string) => Promise<void>;
}> = ({ detail, onAddNote, onAssign, onUnassign }) => {
  const [note, setNote] = useState('');
  const [pickerType, setPickerType] = useState<'property' | 'customer' | 'dealer' | 'person'>('property');
  const [pickerId, setPickerId] = useState('');
  const [opts, setOpts] = useState<any[]>([]);

  React.useEffect(() => {
    const url = pickerType === 'property' ? '/properties'
              : pickerType === 'customer' ? '/customers'
              : pickerType === 'dealer'   ? '/dealers'
              :                              '/tenants';
    fetch(`${API}${url}`, { headers: authHeaders() }).then(r => r.json()).then((d) => setOpts(Array.isArray(d) ? d : (d.items || []))).catch(() => setOpts([]));
  }, [pickerType]);

  const nameOf = (o: any) => o.name || `${o.first_name || ''} ${o.last_name || ''}`.trim() || o.email || o.id;

  return (
    <div className="border-t border-gray-200 pt-3 mt-3 space-y-3">
      {/* Assignments */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-wide text-gray-500">Assigned to</span>
          <span className="text-xs text-gray-400">{(detail.assignments || []).length} link(s)</span>
        </div>
        {(detail.assignments || []).length === 0 && <div className="text-xs text-gray-400 mb-2">Not assigned to any entity yet.</div>}
        <div className="flex flex-wrap gap-2 mb-2">
          {(detail.assignments || []).map((a: any) => (
            <span key={a.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded text-xs">
              <span className="font-medium capitalize">{a.entity_type}:</span>
              <span>{a.entity_name || a.entity_id?.slice(0, 8)}</span>
              <button onClick={() => onUnassign(a.id)} className="ml-1 text-emerald-700 hover:text-red-600">×</button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select value={pickerType} onChange={(e) => { setPickerType(e.target.value as any); setPickerId(''); }}
            className="border border-gray-300 rounded px-2 py-1 text-xs">
            <option value="property">Property</option>
            <option value="customer">Customer</option>
            <option value="dealer">Dealer</option>
            <option value="person">Person</option>
          </select>
          <select value={pickerId} onChange={(e) => setPickerId(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-xs flex-1">
            <option value="">— pick one —</option>
            {opts.map((o: any) => <option key={o.uuid || o.id} value={o.uuid || o.id}>{nameOf(o)}</option>)}
          </select>
          <Button size="sm" disabled={!pickerId} onClick={() => {
            const o = opts.find((x: any) => (x.uuid || x.id) === pickerId);
            onAssign(pickerType, pickerId, o ? nameOf(o) : '');
            setPickerId('');
          }}>Assign</Button>
        </div>
      </div>

      {/* Notes */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-wide text-gray-500">Internal notes</span>
          <span className="text-xs text-gray-400">{(detail.notes || []).length}</span>
        </div>
        <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
          {(detail.notes || []).map((n: any) => (
            <div key={n.id} className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-xs">
              <div className="text-gray-500 text-[10px]">{n.author_email || 'admin'} · {new Date(n.created_at).toLocaleString()}</div>
              <div className="whitespace-pre-wrap text-gray-800">{n.body}</div>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an internal note (only admins see this)…"
            className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
          <Button size="sm" disabled={!note.trim()} onClick={async () => { await onAddNote(note); setNote(''); }}>+ Note</Button>
        </div>
      </div>
    </div>
  );
};
